const AnimationMixin = {
  initAnimation(customConfig = {}) {
    this.animationConfig = {
      easing: 'cubic-bezier(0, 0, 0.12, 0.88)',
      duration: parseFloat(this.getAttribute('data-duration')) || 0.35,
      staggerDuration: parseFloat(this.getAttribute('data-stagger-duration')) || 0.2,
      stageDelay: parseFloat(this.getAttribute('data-stagger-delay')) || 0.05,
      minHeight: 0,
      openedPosition: 'absolute',
      initialOpacity: 0,
      ...customConfig
    };

    this.motion = window.motion || {};
    this.isAnimating = false;
    this._mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._supportsAnimation = !!this.motion.animate && !this._mql.matches;
    this._onMotionChange = () => {
      this._supportsAnimation = !!this.motion.animate && !this._mql.matches;
    };
    this._mql.addEventListener('change', this._onMotionChange);
    this.isRTL = document.documentElement.dir === 'rtl';
  },

  getAnimationConfig(options = {}) {
    return { ...this.animationConfig, ...options };
  },

  animateStagger(element, options = {}) {
    if (!this._supportsAnimation) return;
    const {
      staggerSelector,
      staggerLimit,
      animationType = 'fade',
      slideDirection = 'left',
      delay = 0,
      perItemAnimation = false,
      cleanup = true,
      willChange = true,
      addEndClass = false,
      endClassName = 'animations-end',
      hideOverLimit = false,
      y = '20px',
      isMobile = false,
      initialOpacity,
      staggerDuration,
      stageDelay,
      easing
    } = this.getAnimationConfig(options);

    if (!staggerSelector || !this.motion.stagger) return;
    const staggerItems = element.querySelectorAll(staggerSelector);
    if (!staggerItems.length) return;

    let itemsArray = Array.from(staggerItems);
    let itemsOverLimit = [];

    if (staggerLimit && staggerItems.length > staggerLimit) {
      itemsOverLimit = itemsArray.slice(staggerLimit);
      itemsArray = itemsArray.slice(0, staggerLimit);

      if (hideOverLimit) {
        itemsOverLimit.forEach(item => item.style.visibility = 'hidden');
      }
    }

    let initialProps = {};
    let animateProps = {};

    switch (animationType) {
      case 'fade':
        const yValue = isMobile ? '0' : y;
        initialProps = {
          transform: isMobile ? 'none' : `translateY(${yValue})`,
          opacity: initialOpacity
        };
        animateProps = { opacity: [initialOpacity, 1] };
        if (!isMobile) {
          animateProps.y = [yValue.includes('%') ? yValue : parseFloat(yValue), 0];
        }
        break;

      case 'slide':
        const slideDir = this.isRTL && slideDirection !== 'left' ? this.flipDirection(slideDirection) : slideDirection;
        const slideTransform = slideDir === 'right' ? 'translateX(50px)' : 'translateX(-50px)';
        initialProps = { transform: slideTransform, opacity: initialOpacity };
        animateProps = {
          transform: [slideTransform, 'translateX(0)'],
          opacity: [initialOpacity, 1]
        };
        break;

      case 'slideSm':
        const slideDirSm = this.isRTL && slideDirection !== 'left' ? this.flipDirection(slideDirection) : slideDirection;
        const slideSmTransform = slideDirSm === 'right' ? 'translateX(30px)' : 'translateX(-30px)';
        initialProps = { transform: slideSmTransform, opacity: initialOpacity };
        animateProps = {
          transform: [slideSmTransform, 'translateX(0)'],
          opacity: [initialOpacity, 1]
        };
        break;
    }

    if (willChange) element.style.willChange = 'contents';

    itemsArray.forEach(item => {
      Object.entries(initialProps).forEach(([prop, value]) => {
        if (value !== 'none') item.style[prop] = value;
      });
    });

    setTimeout(() => {
      if (perItemAnimation) {
        const animations = itemsArray.map((item, index) => {
          return this.motion.animate(item, animateProps, {
            duration: staggerDuration,
            delay: stageDelay * index,
            easing
          });
        });

        Promise.all(animations.map(a => a.finished)).then(() => {
          requestAnimationFrame(() => {
            if (cleanup) {
              itemsArray.forEach(item => {
                item.style.transform = '';
                item.style.opacity = '';
              });
            }
            if (willChange) element.style.willChange = '';
            if (addEndClass) element.classList.add(endClassName);
            if (hideOverLimit) {
              itemsOverLimit.forEach(item => item.style.visibility = 'visible');
            }
          });
        });
      } else {
        const animation = this.motion.animate(itemsArray, animateProps, {
          duration: staggerDuration,
          delay: this.motion.stagger(stageDelay),
          easing
        });

        animation.finished.then(() => {
          requestAnimationFrame(() => {
            if (cleanup) {
              itemsArray.forEach(item => {
                item.style.transform = '';
                item.style.opacity = '';
              });
            }
            if (willChange) element.style.willChange = '';
            if (addEndClass) element.classList.add(endClassName);
            if (hideOverLimit) {
              itemsOverLimit.forEach(item => item.style.visibility = 'visible');
            }
          });
        });
      }
    }, delay);
  },

  async animateShow(element, options = {}, skipIfAnimating = true) {
    if (!this._supportsAnimation || (skipIfAnimating && this.isAnimating)) {
      const { onStart = null, onComplete = null, backDrop = false } = options;
      if (backDrop) {
        backDrop.style.opacity = '1';
        backDrop.style.display = 'block';
      }
      onStart?.();
      onComplete?.();
      return;
    }

    this.isAnimating = true;

    const {
      staggerSelector = null,
      animationType = 'slide',
      slideDirection = 'right',
      staggerLimit = false,
      onStart = null,
      onComplete = null,
      heightAnimation = true,
      maxHeight = null,
      backDrop = false,
      hideOverLimit = false,
      openedPosition,
      minHeight,
      duration,
      easing,
      initialOpacity,
      staggerDuration,
      stageDelay
    } = this.getAnimationConfig(options);

    let elementHeight = 0;
    if (heightAnimation) {
      const prev = element.getAttribute('style');
      element.style.cssText += `visibility:hidden;display:block;position:${openedPosition};height:auto;`;
      elementHeight = element.offsetHeight;
      element.setAttribute('style', prev || '');
    }

    const currentHeight = maxHeight !== null ? Math.min(elementHeight, maxHeight) : elementHeight;

    if (heightAnimation) {
      element.style.cssText += `height:${minHeight}px;overflow:hidden;opacity:0;`;
    }

    onStart?.();

    const heightBased = (currentHeight / 300) * duration;
    const animDuration = heightBased > duration * 1.5 ? duration * 1.5 : heightBased < 0.25 ? 0.25 : heightBased;

    const animationProps = { opacity: [heightAnimation ? 0.9 : 0, 1] };
    if (heightAnimation) {
      animationProps.height = [`${minHeight}px`, `${currentHeight}px`];
    }

    const contentAnimation = this.motion.animate(element, animationProps, {
      duration: animDuration,
      easing
    });

    if (backDrop) {
      backDrop.style.display = 'block';
      this.motion.animate(backDrop, { opacity: [0, 1] }, { duration: animDuration, easing });
    }

    if (staggerSelector) {
      this.animateStagger(element, {
        staggerSelector,
        staggerLimit,
        animationType,
        slideDirection,
        initialOpacity,
        staggerDuration,
        stageDelay,
        hideOverLimit
      });
    }

    contentAnimation.finished.finally(() => {
      if (heightAnimation) {
        ['height', 'overflow'].forEach(prop => element.style.removeProperty(prop));
      }
      element.style.removeProperty('opacity');
      this.isAnimating = false;
      onComplete?.();
    });
  },

  async animateHide(element, options = {}, skipIfAnimating = true) {
    if (!this._supportsAnimation || (skipIfAnimating && this.isAnimating)) {
      const { onStart = null, onComplete = null, backDrop = false } = options;
      if (backDrop) {
        backDrop.style.opacity = '';
        backDrop.style.display = '';
      }
      onStart?.();
      onComplete?.();
      return;
    }

    this.isAnimating = true;
    this.classList.add('is-closing');

    const {
      onStart = null,
      onComplete = null,
      heightAnimation = false,
      backDrop = false,
      duration,
      easing,
      minHeight
    } = this.getAnimationConfig(options);

    onStart?.();

    const currentHeight = element.offsetHeight;
    element.style.overflow = 'hidden';

    const heightBased = (currentHeight / 300) * duration;
    let animDuration = heightBased > duration * 1.5 ? duration * 1.5 : heightBased < 0.2 ? 0.2 : heightBased;

    const animationProps = { opacity: [1, heightAnimation ? 0.9 : 0] };

    if (heightAnimation) {
      animationProps.height = [`${currentHeight}px`, `${minHeight}px`];
      animDuration = animDuration * 2;
    }

    const hideAnimation = this.motion.animate(element, animationProps, {
      duration: animDuration * 0.5,
      easing
    });

    if (backDrop) {
      this.motion.animate(backDrop, { opacity: [1, 0] }, { duration: animDuration * 0.5, easing });
    }

    hideAnimation.finished.finally(() => {
      ['height', 'opacity', 'overflow', 'transform', 'visibility'].forEach(prop => element.style.removeProperty(prop));
      this.isAnimating = false;
      this.classList.remove('is-closing');
      if (backDrop) backDrop.style.display = '';
      onComplete?.();
    });
  },

  flipDirection(direction) {
    const directionMap = {
      'left': 'right',
      'right': 'left'
    };
    return directionMap[direction] || direction;
  },

  getTranslateValue(direction, amount) {
    const isLTR = !this.isRTL;
    const sign = (direction === 'left' && isLTR) || (direction === 'right' && !isLTR) ? '-' : '';
    return `translateX(${sign}${amount})`;
  },

  async animateSlideIn(element, options = {}) {
    const { onStart = null, onComplete = null, backDrop = false } = options;

    if (!this._supportsAnimation || this.isAnimating) {
      if (backDrop) {
        backDrop.style.display = 'block';
        backDrop.style.opacity = '1';
      }
      onStart?.();
      onComplete?.();
      return;
    }

    this.isAnimating = true;

    const {
      slideDirection = 'left',
      animationType = 'slide',
      staggerSelector = null,
      duration = 0.35,
      easing,
      staggerDuration,
      stageDelay
    } = this.getAnimationConfig(options);

    onStart?.();

    if (backDrop) {
      backDrop.style.display = 'block';
      this.motion.animate(backDrop, { opacity: [0, 1] }, { duration, easing });
    }

    const finalSlideDirection = this.isRTL && (slideDirection === 'left' || slideDirection === 'right')
      ? this.flipDirection(slideDirection)
      : slideDirection;

    let transform;
    switch (finalSlideDirection) {
      case 'right': transform = 'translateX(100%)'; break;
      case 'left': transform = 'translateX(-100%)'; break;
      case 'top': transform = 'translateY(-30%)'; break;
      case 'bottom': transform = 'translateY(30%)'; break;
      case 'center': transform = 'scale(0.7)'; break;
      default: transform = 'translateX(-100%)';
    }

    element.style.cssText += `transform: ${transform};visibility: hidden;`;
    const fadeInDirections = new Set(['top', 'center']);
    const startOpacity = fadeInDirections.has(finalSlideDirection) ? 0 : 1;

    const animationProps = {
      transform: [transform, 'translate(0, 0)'],
      opacity: [startOpacity, 1],
      visibility: ['hidden', 'visible']
    };

    const slideAnimation = this.motion.animate(element, animationProps, { duration, easing });

    if (staggerSelector) {
      this.animateStagger(element, {
        staggerSelector,
        animationType,
        slideDirection,
        delay: finalSlideDirection === 'top' ? 0 : 100,
        staggerDuration,
        stageDelay
      });
    }

    slideAnimation.finished.finally(() => {
      ['opacity', 'transform', 'visibility'].forEach(prop => element.style.removeProperty(prop));
      this.isAnimating = false;
      onComplete?.();
      let menu = document.querySelector('.menu-drawer__inner-container');
    });
  },

  async animateSlideOut(element, options = {}) {
    const { onStart = null, onComplete = null, backDrop = false } = options;

    if (!this._supportsAnimation || this.isAnimating) {
      if (backDrop) {
        backDrop.style.opacity = '';
        backDrop.style.display = '';
      }
      onStart?.();
      onComplete?.();
      return;
    }

    this.isAnimating = true;
    this.classList.add('is-closing');
    const isMobile = DeviceUtils.isMobile;

    const {
      slideDirection = 'left',
      durationOut = isMobile ? 0.25 : 0.2,
      easing
    } = this.getAnimationConfig(options);

    onStart?.();

    if (backDrop) {
      this.motion.animate(backDrop, { opacity: [1, 0] }, { durationOut, easing });
    }

    const finalSlideDirection = this.isRTL && (slideDirection === 'left' || slideDirection === 'right')
      ? this.flipDirection(slideDirection)
      : slideDirection;

    let finalTransform;
    switch (finalSlideDirection) {
      case 'right': finalTransform = isMobile ? 'translateX(-100%)' : 'translateX(-50%)'; break;
      case 'left': finalTransform = isMobile ? 'translateX(100%)' : 'translateX(50%)'; break;
      case 'top': finalTransform = 'translateY(-20%)'; break;
      case 'bottom': finalTransform = 'translateY(20%)'; break;
      case 'center': finalTransform = 'scale(1.1)'; break;
      default: finalTransform = isMobile ? 'translateX(100%)' : 'translateX(50%)';
    }

    const keepFinalOpacity = isMobile && (finalSlideDirection == 'right' || finalSlideDirection == 'left');

    const animationProps = {
      transform: ['translate(0, 0)', finalTransform],
      opacity: [1, keepFinalOpacity ? 1 : 0],
      visibility: ['visible', 'hidden']
    };

    const slideAnimation = this.motion.animate(element, animationProps, { durationOut, easing });

    slideAnimation.finished.finally(() => {
      ['opacity', 'transform', 'visibility'].forEach(prop => element.style.removeProperty(prop));
      this.isAnimating = false;
      this.classList.remove('is-closing');
      if (backDrop) backDrop.style.display = '';
      onComplete?.();
    });
  }
};

function withAnimation(BaseClass) {
  return class extends BaseClass {
    constructor(...args) {
      super(...args);
      Object.assign(this, AnimationMixin);
    }
  };
}

class AnimatedList extends withAnimation(HTMLElement) {
  static instances = new Set();
  static resizeListenerAttached = false;
  static resizeRaf = null;

  static handleGlobalResize() {
    if (AnimatedList.resizeRaf) return;
    AnimatedList.resizeRaf = requestAnimationFrame(() => {
      AnimatedList.resizeRaf = null;
      AnimatedList.instances.forEach((instance) => {
        if (!instance?.isConnected) return;
        instance.handleResizeBound?.();
      });
    });
  }

  static attachResizeListener() {
    if (AnimatedList.resizeListenerAttached) return;
    window.addEventListener('resize', AnimatedList.handleGlobalResize);
    AnimatedList.resizeListenerAttached = true;
  }

  static detachResizeListenerIfIdle() {
    if (AnimatedList.instances.size > 0 || !AnimatedList.resizeListenerAttached) return;
    window.removeEventListener('resize', AnimatedList.handleGlobalResize);
    AnimatedList.resizeListenerAttached = false;
    if (AnimatedList.resizeRaf) {
      cancelAnimationFrame(AnimatedList.resizeRaf);
      AnimatedList.resizeRaf = null;
    }
  }

  constructor(options = {}) {
    super();

    this.options = {
      duration: 0.25,
      panelSelector: '.animated-list__list',
      toggleButtonSelector: '.animated-list__button',
      closeButtonSelector: '.close-button',
      backdropSelector: '.backdrop',
      searchInputSelector: 'input[type="search"]',
      resetButtonSelector: '.reset-button',
      focusableItemsSelector: 'input[type="checkbox"]',
      scrollTrackSelector: 'custom-scrollbar',
      containerSelector: '.menu-drawer',
      staggerSelector: 'ul li:not(.hidden,.off-stage)',
      staggerLimit: false,
      trapFocus: true,
      initialOpacity: 0,
      staggerDuration: 0.2,
      stageDelay: 0.03,
      ...options
    };
    this.preventAutoClose = false;
    this.elements = this.initElements();
    this.focusTrap = null;

    this.initAnimation({
      duration: this.options.duration
    });

    this.handleToggle = this.toggle.bind(this);
    this.handleClose = this.close.bind(this);
    this.handleBackdrop = this.handleBackdropClick.bind(this);
    this.handleReset = this.resetSearch.bind(this);
    this.handleResetMouseDown = (event) => event.preventDefault();
    this.handleKey = this.handleKeyDown.bind(this);
    this.handleFocus = this.handleFocusOut.bind(this);
    this.handleResizeBound = this.handleResize.bind(this);
    this.handleOutsideClick = this.onOutsideClick.bind(this);
  }

  connectedCallback() {
    AnimatedList.instances.add(this);
    AnimatedList.attachResizeListener();

    this.elements.toggleButton?.addEventListener('click', this.handleToggle);
    this.elements.closeButton?.addEventListener('click', this.handleClose);
    this.elements.backdrop?.addEventListener('click', this.handleBackdrop);
    this.elements.resetButton?.addEventListener('click', this.handleReset);
    this.elements.resetButton?.addEventListener('mousedown', this.handleResetMouseDown);

    if (this.options.trapFocus) {
      this.addEventListener('keydown', this.handleKey);
      this.addEventListener('focusout', this.handleFocus);
    }
    if (!window.DeviceUtils?.isMobile) {
      document.addEventListener('pointerdown', this.handleOutsideClick);
    }
  }

  disconnectedCallback() {
    AnimatedList.instances.delete(this);
    AnimatedList.detachResizeListenerIfIdle();

    this.elements.toggleButton?.removeEventListener('click', this.handleToggle);
    this.elements.closeButton?.removeEventListener('click', this.handleClose);
    this.elements.backdrop?.removeEventListener('click', this.handleBackdrop);
    this.elements.resetButton?.removeEventListener('click', this.handleReset);
    this.elements.resetButton?.removeEventListener('mousedown', this.handleResetMouseDown);
    document.removeEventListener('pointerdown', this.handleOutsideClick);

    if (this.options.trapFocus) {
      this.removeEventListener('keydown', this.handleKey);
      this.removeEventListener('focusout', this.handleFocus);
    }

    clearTimeout(this.resizeTimeout);
    this._mql?.removeEventListener('change', this._onMotionChange);
  }

  initElements() {
    return {
      panel: this.querySelector(this.options.panelSelector),
      toggleButton: this.querySelector(this.options.toggleButtonSelector),
      closeButton: this.querySelector(this.options.closeButtonSelector),
      backdrop: this.parentElement?.querySelector(this.options.backdropSelector),
      searchInput: this.querySelector(this.options.searchInputSelector),
      resetButton: this.querySelector(this.options.resetButtonSelector),
      scrollTrack: this.querySelector(this.options.scrollTrackSelector),
      container: document.querySelector(this.options.containerSelector)
    };
  }

  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      if (DeviceUtils.isMobileSmall) return;
      this.adjustPanelPosition();
    }, 150);
  }

  get isOpen() {
    return this.elements.toggleButton?.getAttribute('aria-expanded') === 'true';
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    if (this.isOpen) return;

    this.closeOtherMenus();

    this.elements.toggleButton?.setAttribute('aria-expanded', 'true');
    this.elements.panel?.removeAttribute('hidden');

    this.adjustPanelPosition();

    const scrollableFacet = this.querySelector('scrollable-facet');
    if (scrollableFacet) scrollableFacet.updateHeight();

    if (!document.body.classList.contains('overflow-hidden-tablet') && this.options.bodyOverflowClass) {
      document.body.classList.add(this.options.bodyOverflowClass);
    }

    if (this.options.containerSelector && this.options.containerOpenClass) {
      document.querySelector(this.options.containerSelector).classList.add(this.options.containerOpenClass);
    }

    this.animateShowPanel();

    this.elements.searchInput?.focus();
    this.scrollToTop();
    this.elements.scrollTrack?.init();

    if (this.options.trapFocus) {
      this.setupFocusTrap();
    }

    this.dispatchEvent(new CustomEvent('animatedlist:open', { bubbles: true }));
  }

  adjustPanelPosition() {
    const panel = this.elements.panel;
    if (!panel) return;

    const rect = panel.getBoundingClientRect();
    const panelWidth = 280;

    if (this.isRTL) {
      const leftEdge = rect.right - panelWidth;
      if (leftEdge < 0) {
        const overflow = Math.abs(leftEdge);
        const offset = Math.round(overflow + 30);
        this.style.setProperty('--offset-right', `-${offset}px`);
        this.style.removeProperty('--offset-left');
      } else {
        this.style.removeProperty('--offset-right');
        this.style.removeProperty('--offset-left');
      }
    } else {
      const rightEdge = rect.left + panelWidth;
      if (rightEdge > window.innerWidth) {
        const overflow = rightEdge - window.innerWidth;
        const offset = Math.round(overflow + 30);
        this.style.setProperty('--offset-left', `-${offset}px`);
        this.style.removeProperty('--offset-right');
      } else {
        this.style.removeProperty('--offset-left');
        this.style.removeProperty('--offset-right');
      }
    }
  }

  close() {
    if (!this.isOpen) return;
    this.animateHidePanel();
  }

  handleBackdropClick(event) {
    event.preventDefault();
    if (!window.DeviceUtils?.isMobile) return;
    this.close();
  }

  closeOtherMenus() {
    const megaMenu = document.querySelector('mega-menu');
    if (megaMenu?.classList.contains('mega-menu--block')) {
      megaMenu.classList.remove('mega-menu--block');
      document.body.removeAttribute('data-mega-menu-block');
      megaMenu.forceClose?.();
    }

    const filtersGroup = this.closest('facet-filters-form');
    if (!filtersGroup) return;

    const otherLists = filtersGroup.querySelectorAll('animated-list');
    otherLists.forEach(list => {
      if (list !== this && typeof list.forceClose === 'function') {
        list.forceClose();
      }
    });
  }

  hidePanel() {
    this.elements.toggleButton?.setAttribute('aria-expanded', 'false');
    this.elements.panel?.setAttribute('hidden', true);
    this.style.removeProperty('--offset-left');
    this.style.removeProperty('--offset-right');

    if (this.options.bodyOverflowClass) {
      document.body.classList.remove(this.options.bodyOverflowClass);
    }

    if (this.options.containerSelector && this.options.containerOpenClass) {
      document.querySelector(this.options.containerSelector).classList.remove(this.options.containerOpenClass);
    }

    if (this.elements.panel) {
      this.elements.panel.style.height = '';
      this.elements.panel.style.opacity = '';
      this.elements.panel.style.overflow = '';
    }

    this.removeFocusTrap();

    this.dispatchEvent(new CustomEvent('animatedlist:close', { bubbles: true }));
  }

  animateShowPanel() {
    this.animateShow(this.elements.panel, {
      heightAnimation: true,
      staggerSelector: this.options.staggerSelector,
      staggerLimit: this.options.staggerLimit,
      backDrop: window.DeviceUtils?.isMobile ? this.elements.backdrop : false,
      initialOpacity: this.options.initialOpacity,
      staggerDuration: this.options.staggerDuration,
      stageDelay: this.options.stageDelay,
      hideOverLimit: true,
      onStart: () => {
        if (this.elements.scrollTrack) {
          this.elements.scrollTrack.style.opacity = '1';
        }
      }
    }, false);
  }

  animateHidePanel() {
    const isMobile = window.DeviceUtils?.isMobile;
    this.animateHide(this.elements.panel, {
      heightAnimation: isMobile,
      backDrop: isMobile ? this.elements.backdrop : false,
      onStart: () => {
        if (this.elements.scrollTrack) {
          this.elements.scrollTrack.style.opacity = '0';
        }
      },
      onComplete: () => this.hidePanel()
    }, false);
  }

  setupFocusTrap() {
    this.focusTrap = {
      focusableElements: this.getFocusableElements(),
      firstFocusableElement: null,
      lastFocusableElement: null
    };

    const elements = this.focusTrap.focusableElements;
    if (elements.length > 0) {
      this.focusTrap.firstFocusableElement = elements[0];
      this.focusTrap.lastFocusableElement = elements[elements.length - 1];
    }
  }

  removeFocusTrap() {
    this.focusTrap = null;
  }

  getFocusableElements() {
    const focusableSelectors = [
      this.options.focusableItemsSelector,
      'button:not([disabled])',
      'input:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    return Array.from(this.querySelectorAll(focusableSelectors)).filter(element =>
      !element.closest('li.hidden') &&
      !element.hidden &&
      element.offsetParent !== null
    );
  }

  handleKeyDown(event) {
    if (!this.isOpen || !this.options.trapFocus) return;

    const focusableItems = this.getFocusableElements();
    let focusedItemIndex = focusableItems.findIndex(item => item === document.activeElement);
    let itemToFocus;

    const arrowUpKey = this.isRTL ? 'ARROWRIGHT' : 'ARROWUP';
    const arrowDownKey = this.isRTL ? 'ARROWLEFT' : 'ARROWDOWN';

    switch (event.code.toUpperCase()) {
      case 'ESCAPE':
        event.preventDefault();
        event.stopPropagation();
        this.close();
        this.elements.toggleButton?.focus();
        break;

      case 'TAB':
        if (focusableItems.length === 0) {
          event.preventDefault();
          return;
        }

        if (event.shiftKey) {
          if (document.activeElement === this.focusTrap?.firstFocusableElement) {
            event.preventDefault();
            this.focusTrap.lastFocusableElement?.focus();
          }
        } else {
          if (document.activeElement === this.focusTrap?.lastFocusableElement) {
            event.preventDefault();
            this.focusTrap.firstFocusableElement?.focus();
          }
        }
        break;

      case arrowUpKey:
        event.preventDefault();
        itemToFocus = focusedItemIndex > 0
          ? focusableItems[focusedItemIndex - 1]
          : focusableItems[focusableItems.length - 1];
        itemToFocus?.focus();
        break;

      case arrowDownKey:
        event.preventDefault();
        itemToFocus = focusedItemIndex < focusableItems.length - 1
          ? focusableItems[focusedItemIndex + 1]
          : focusableItems[0];
        itemToFocus?.focus();
        break;

      case 'SPACE':
        if (!this.isOpen && document.activeElement === this.elements.toggleButton) {
          event.preventDefault();
          this.open();
        }
        break;
    }
  }

  handleFocusOut() {
    if (!this.isOpen || window.DeviceUtils?.isMobile) return;

    if (this.preventAutoClose) {
      this.focus();
      return;
    }

    setTimeout(() => {
      if (this._pointerDownInside) {
        this._pointerDownInside = false;
        return;
      }
      if (!this.contains(document.activeElement) &&
        !this.elements.backdrop?.contains(document.activeElement)) {
        this.close();
      }
    }, 0);
  }

  onOutsideClick(event) {
    if (!this.isOpen) return;

    const path = event.composedPath?.() || [];

    const isInside =
      path.includes(this) ||
      path.includes(this.elements.panel) ||
      event.target?.closest?.('animated-list') === this;

    this._pointerDownInside = isInside;

    const isToggle = path.includes(this.elements.toggleButton);

    if (!isInside && !isToggle) {
      this.close();
    }
  }

  scrollToTop() {
    const scrollableElements = this.querySelectorAll('[class*="scroll"], .list');
    scrollableElements.forEach(element => {
      element.scrollTop = 0;
    });
  }

  resetSearch() {
    if (this.elements.searchInput) {
      this.elements.searchInput.value = '';
      this.elements.searchInput.focus();

      this.elements.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  forceClose() {
    if (!this.isOpen) return;

    this.hidePanel();

    if (this.elements.backdrop) {
      this.elements.backdrop.style.opacity = '0';
      this.elements.backdrop.style.display = '';
    }
  }

  updateFocusTrap() {
    if (this.focusTrap && this.isOpen) {
      this.setupFocusTrap();
    }
  }

  focusItem(index) {
    const focusableItems = this.getFocusableElements();
    if (focusableItems[index]) {
      focusableItems[index].focus();
    }
  }

  destroy() {
    this.forceClose();
    this.removeFocusTrap();
  }
}

customElements.define('animated-list', AnimatedList);